/**
 * Appeal document stream_spawn API — Document Layer I3
 */

import type {
  DocumentExecutorInput,
  DocumentExecutorResult,
} from '@shared/document/resolve-executor';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { getAuthHeaders } from '../utils/jwtUtils';
import { fetchWithAuth } from './authFetch';

const APPEAL_BASE = '/api/domain/appeal';

async function postSpawn<T>(
  tenantId: string,
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const json = (await res.json()) as { success?: boolean; data?: T; error?: string };
  if (!res.ok || json.success === false) {
    throw new Error(json.error ?? `Stream spawn failed: ${res.status}`);
  }
  return json.data as T;
}

export async function spawnEphemeralPerformanceStream(
  tenantId: string,
  performanceId: string
): Promise<unknown> {
  return postSpawn(
    tenantId,
    `${APPEAL_BASE}/ops/performance/${encodeURIComponent(performanceId)}/spawn-stream`,
    {}
  );
}

export async function setupRecurrentPerformanceStream(
  tenantId: string,
  activityId: string,
  params: {
    streamTemplateId: string;
    recurrencePattern?: Record<string, unknown>;
    startDate?: string;
  }
): Promise<unknown> {
  return postSpawn(
    tenantId,
    `${APPEAL_BASE}/recurrent_performance/ops/${encodeURIComponent(activityId)}/setup-recurring`,
    params
  );
}

export async function spawnAppealDocumentStream(
  input: DocumentExecutorInput
): Promise<DocumentExecutorResult> {
  const recordId = String(
    input.payload.recordId ?? input.payload.performanceId ?? input.payload.activityId ?? ''
  );

  if (!recordId) {
    return { ok: false, error: 'recordId required for stream spawn' };
  }

  try {
    if (input.documentType === 'ephemeral_performance') {
      const data = await spawnEphemeralPerformanceStream(input.tenantId, recordId);
      return { ok: true, data };
    }

    if (input.documentType === 'recurrent_performance') {
      const streamTemplateId =
        typeof input.payload.streamTemplateId === 'string' ? input.payload.streamTemplateId : '';
      if (!streamTemplateId) {
        return { ok: false, error: 'streamTemplateId required for recurrent stream spawn' };
      }
      const data = await setupRecurrentPerformanceStream(input.tenantId, recordId, {
        streamTemplateId,
        ...(input.payload.recurrencePattern !== undefined
          ? { recurrencePattern: input.payload.recurrencePattern as Record<string, unknown> }
          : {}),
      });
      return { ok: true, data };
    }

    return { ok: false, error: `Unsupported stream_spawn document type: ${input.documentType}` };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Stream spawn failed';
    return { ok: false, error: message };
  }
}
